import { baseKeymap, toggleMark } from "prosemirror-commands";
import { dropCursor } from "prosemirror-dropcursor";
import { gapCursor } from "prosemirror-gapcursor";
import { history, redo, undo } from "prosemirror-history";
import { ellipsis, emDash, inputRules, smartQuotes } from "prosemirror-inputrules";
import { keymap } from "prosemirror-keymap";
import { type MenuElement, menuBar as toolbar } from "prosemirror-menu";
import type { Command, EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { schema } from "../adapter/schema";
import { backspaceCommand, enterCommand, spaceCommand } from "../commands";
import { alignmentItems } from "../toolbar/alignment";
import { toggleBlockTypeItem } from "../toolbar/block-toggle";
import { DynamicColorPicker } from "../toolbar/color-picker";
import { formulaItem } from "../toolbar/formula";
import { imageItem } from "../toolbar/image";
import { addIndent, indentDecreaseItem, indentIncreaseItem, removeIndent } from "../toolbar/indent";
import { linkItem } from "../toolbar/link";
import { markItem } from "../toolbar/mark-toggle";
import { DynamicTextStyleDropdown } from "../toolbar/text-style-dropdown";
import { collapseToPanelZeroItem, expandToPanelOneItem } from "../toolbar/toolbar-panel";
import { isFormulaSelected, isImageSelected, isSelectionInCodeBlock, modKey } from "../utils";

import { codeBlockMergePlugin } from "./code-block-merge";
import { toolbarPanelPlugin, toolbarPanelPluginKey } from "./toolbar-panel";

import { t } from "i18n/i18n";
import {
  toolbarAlignCenterIcon,
  toolbarAlignLeftIcon,
  toolbarAlignRightIcon,
  toolbarBoldIcon,
  toolbarBulletListIcon,
  toolbarCodeBlockIcon,
  toolbarItalicIcon,
  toolbarNumberedListIcon,
  toolbarQuoteIcon,
  toolbarUnderlineIcon,
} from "mds/utils/toolbar-icons";

function buildInputRules() {
  const rules = smartQuotes.concat(ellipsis, emDash);
  return inputRules({ rules });
}

/**
 * Build keyboard shortcuts for the editor.
 * "Mod" is a platform-agnostic modifier key (Cmd on Mac, Ctrl on Windows/Linux)
 */
function buildKeymap(): Record<string, Command> {
  const keys: Record<string, Command> = {};

  // History shortcuts: undo and redo
  keys["Mod-z"] = undo; // Cmd/Ctrl+Z
  keys["Shift-Mod-z"] = redo; // Cmd/Ctrl+Shift+Z

  // Mark shortcuts: toggle formatting with keyboard
  if (schema.marks.bold) {
    const toggleBold = (state, dispatch) =>
      isSelectionInCodeBlock(state) || isFormulaSelected(state) || isImageSelected(state)
        ? false
        : toggleMark(schema.marks.bold)(state, dispatch);
    keys["Mod-b"] = toggleBold;
    keys["Mod-B"] = toggleBold;
  }
  if (schema.marks.italic) {
    const toggleItalic = (state, dispatch) =>
      isSelectionInCodeBlock(state) || isFormulaSelected(state) || isImageSelected(state)
        ? false
        : toggleMark(schema.marks.italic)(state, dispatch);
    keys["Mod-i"] = toggleItalic;
    keys["Mod-I"] = toggleItalic;
  }
  if (schema.marks.underline) {
    const toggleUnderline = (state, dispatch) =>
      isSelectionInCodeBlock(state) || isFormulaSelected(state) || isImageSelected(state)
        ? false
        : toggleMark(schema.marks.underline)(state, dispatch);
    keys["Mod-u"] = toggleUnderline;
    keys["Mod-U"] = toggleUnderline;
  }

  // Indent shortcuts
  keys["Mod-]"] = addIndent; // Cmd/Ctrl+]
  keys["Mod-["] = removeIndent; // Cmd/Ctrl+[

  // custom handling for list items
  keys.Enter = enterCommand;
  keys.Space = spaceCommand;
  for (const bsKey of ["Backspace", "Mod-Backspace", "Alt-Backspace", "Shift-Backspace"]) {
    keys[bsKey] = backspaceCommand;
  }

  return keys;
}

/**
 * Menu item creation helpers
 *
 * These functions create menu items for the toolbar, each performing a schema check
 * and returning the appropriate menu element or null if the schema doesn't support it.
 */

function createTextStyleDropdownItem(): MenuElement | null {
  if (!schema.nodes.heading || !schema.nodes.paragraph) return null;
  return new DynamicTextStyleDropdown();
}

function createBoldItem(): MenuElement | null {
  if (!schema.marks.bold) return null;

  return markItem(schema.marks.bold, {
    title: `${t("worksheet.rich_text_editor.toggle_bold")} (${modKey}+B)`,
    icon: toolbarBoldIcon,
  });
}

function createItalicItem(): MenuElement | null {
  if (!schema.marks.italic) return null;

  return markItem(schema.marks.italic, {
    title: `${t("worksheet.rich_text_editor.toggle_italic")} (${modKey}+I)`,
    icon: toolbarItalicIcon,
  });
}

function createUnderlineItem(): MenuElement | null {
  if (!schema.marks.underline) return null;

  return markItem(schema.marks.underline, {
    title: `${t("worksheet.rich_text_editor.toggle_underline")} (${modKey}+U)`,
    icon: toolbarUnderlineIcon,
  });
}

function createLinkItem(): MenuElement | null {
  if (!schema.marks.link) return null;
  return linkItem(schema.marks.link);
}

function createImageItem(): MenuElement | null {
  if (!schema.nodes.image) return null;
  return imageItem();
}

function createFormulaItem(): MenuElement | null {
  if (!schema.nodes.formula) return null;
  return formulaItem();
}

function createQuoteItem(): MenuElement | null {
  if (!schema.nodes.quote) return null;

  return toggleBlockTypeItem(schema.nodes.quote, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.toggle_quote"),
    icon: toolbarQuoteIcon,
  });
}

function createCodeBlockItem(): MenuElement | null {
  if (!schema.nodes.code_block) return null;

  return toggleBlockTypeItem(schema.nodes.code_block, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.toggle_code_block"),
    icon: toolbarCodeBlockIcon,
  });
}

function createOrderedListItem(): MenuElement | null {
  if (!schema.nodes.ordered_list_item) return null;

  return toggleBlockTypeItem(schema.nodes.ordered_list_item, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.ordered_list_item"),
    icon: toolbarNumberedListIcon,
  });
}

function createBulletListItem(): MenuElement | null {
  if (!schema.nodes.bullet_list_item) return null;

  return toggleBlockTypeItem(schema.nodes.bullet_list_item, schema.nodes.paragraph, {
    title: t("worksheet.rich_text_editor.bullet_list_item"),
    icon: toolbarBulletListIcon,
  });
}

function createIndentIncreaseItem(): MenuElement | null {
  return indentIncreaseItem;
}

function createIndentDecreaseItem(): MenuElement | null {
  return indentDecreaseItem;
}

function createColorPickerItem(): MenuElement | null {
  if (!schema.marks.color) return null;
  return new DynamicColorPicker();
}

function createAlignmentItems(): MenuElement[] {
  return alignmentItems([
    {
      value: "left",
      icon: toolbarAlignLeftIcon,
      title: t("worksheet.rich_text_editor.align_left"),
    },
    {
      value: "center",
      icon: toolbarAlignCenterIcon,
      title: t("worksheet.rich_text_editor.align_center"),
    },
    {
      value: "right",
      icon: toolbarAlignRightIcon,
      title: t("worksheet.rich_text_editor.align_right"),
    },
  ]);
}

function buildGroup(...creators: Array<() => MenuElement | MenuElement[] | null>): MenuElement[] {
  // Creators may return a single item, an array of items, or null.
  return creators.flatMap((creator) => creator() ?? []);
}

function withPanelVisibility(element: MenuElement, panel: number): MenuElement {
  return {
    render(view: EditorView) {
      const rendered = element.render(view);
      return {
        ...rendered,
        update(state: EditorState) {
          if ((toolbarPanelPluginKey.getState(state)?.activePanel ?? 0) !== panel) {
            return false; // signals prosemirror-menu to set display:none on the item
          }
          return rendered.update(state);
        },
      };
    },
  };
}

/**
 * Items are organized into four groups across two panels:
 * - Panel 0: groups 1 and 2 — basic formatting
 * - Panel 1: groups 3 and 4 — advanced formatting
 */
function buildToolbar(): MenuElement[][] {
  const configureForPanel0 = (item: MenuElement) => withPanelVisibility(item, 0);
  const configureForPanel1 = (item: MenuElement) => withPanelVisibility(item, 1);

  return [
    // Group 1: expand button + Text style, Bold, Italic, Underline
    [
      expandToPanelOneItem,
      ...buildGroup(
        createTextStyleDropdownItem,
        createBoldItem,
        createItalicItem,
        createUnderlineItem,
      ).map(configureForPanel0),
    ],
    // Group 2: Bullet list, Numbered list, Indent, Outdent, Text color
    buildGroup(
      createBulletListItem,
      createOrderedListItem,
      createIndentIncreaseItem,
      createIndentDecreaseItem,
      createColorPickerItem,
    ).map(configureForPanel0),
    // Group 3: collapse button + Link, Image, Quote, Code block, Formula
    [
      collapseToPanelZeroItem,
      ...buildGroup(
        createLinkItem,
        createImageItem,
        createQuoteItem,
        createCodeBlockItem,
        createFormulaItem,
      ).map(configureForPanel1),
    ],
    // Group 4: Align left, Align center, Align right
    buildGroup(createAlignmentItems).map(configureForPanel1),
  ];
}

/** Assembles the editor's plugin configuration: input rules, keyboard shortcuts, core behaviors, and optionally the toolbar. */
export function createBasePlugins(showToolbar: boolean) {
  const plugins = [
    buildInputRules(), // auto-transforms text as you type: smart quotes, em dashes, ellipsis
    keymap(buildKeymap()), // Cmd+B bold, Cmd+Z undo, etc.
    keymap(baseKeymap), // Enter, Backspace, arrow keys
    dropCursor(), // visual indicator when dragging content
    gapCursor(), // click into gaps between block nodes
    history(), // undo/redo
    codeBlockMergePlugin(), // merge adjacent code blocks after a transaction
  ];

  if (showToolbar) {
    plugins.push(
      toolbarPanelPlugin(),
      toolbar({
        floating: false, // Fixed at the bottom
        position: "after", // Below the editor
        content: buildToolbar(),
      }),
    );
  }

  return plugins;
}
